import React from 'react';
import useAppVersion from '../hooks/useAppVersion';
import { resolveDisplayVersion } from '../utils/appVersion';

/**
 * AppFooter — small, fixed footer rendered globally on every authenticated
 * portal page so support tickets always include the version + build date
 * the user was on. Pulls the live version from ``/api/system/version`` via
 * ``useAppVersion``.
 *
 * Behaviour on first paint:
 *  - While the very first fetch is in-flight we render a subtle skeleton so
 *    we never flash a fallback and then flip to a different DB value.
 *  - Once the fetch resolves we display ``max(fallback, dbVersion)`` — the
 *    fallback represents the version baked into the currently-running JS
 *    bundle, so a DB that is behind the shipped code cannot downgrade the
 *    footer.
 *  - If the fetch fails we still show the fallback so the footer is never
 *    blank.
 *
 * Bump the constants in ``utils/appVersion.js`` on every release.
 */
const AppFooter = () => {
  const { version, publishedAt, loading } = useAppVersion();
  const { version: displayVersion, build } = resolveDisplayVersion(version, publishedAt);

  if (loading) {
    return (
      <div
        data-testid="app-footer"
        aria-busy="true"
        className="fixed bottom-2 right-3 z-40 select-none pointer-events-none
                   text-[10px] leading-none tracking-wide
                   bg-white/70 backdrop-blur-sm border border-slate-200/70
                   rounded-full px-2.5 py-1 shadow-sm flex items-center gap-1.5"
      >
        <span
          data-testid="app-footer-skeleton"
          className="inline-block h-2 w-14 rounded-full bg-slate-200/80 animate-pulse"
        />
        <span className="text-slate-300">·</span>
        <span className="inline-block h-2 w-16 rounded-full bg-slate-200/80 animate-pulse" />
      </div>
    );
  }

  return (
    <div
      data-testid="app-footer"
      className="fixed bottom-2 right-3 z-40 select-none pointer-events-none
                 text-[10px] leading-none tracking-wide
                 text-slate-400/80 hover:text-slate-500
                 bg-white/70 backdrop-blur-sm border border-slate-200/70
                 rounded-full px-2.5 py-1 shadow-sm"
    >
      <span data-testid="app-footer-version">v{displayVersion}</span>
      {build && (
        <>
          <span className="mx-1.5 text-slate-300">·</span>
          <span data-testid="app-footer-build">build {build}</span>
        </>
      )}
    </div>
  );
};

export default AppFooter;
